import { useState, useEffect, useCallback } from 'react';
import {
  CURRENCIES,
  getRatesSnapshot,
  getRatesMeta,
  loadExchangeRates,
  hasLiveRates,
} from '@/lib/currency';

type RateSource = 'live' | 'static' | 'cache';

export default function ExchangeRatesPage() {
  const [snapshot, setSnapshot] = useState(getRatesSnapshot());
  const [meta, setMeta] = useState<{ updatedAt: number | null; source: RateSource }>(getRatesMeta());
  const [light, setLight] = useState(hasLiveRates());
  const [refreshing, setRefreshing] = useState(false);
  const [lastResult, setLastResult] = useState<'ok' | 'fail' | null>(null);

  const sync = useCallback(() => {
    setSnapshot(getRatesSnapshot());
    setMeta(getRatesMeta());
    setLight(hasLiveRates());
  }, []);

  useEffect(() => {
    loadExchangeRates().finally(sync);
    const onRates = () => sync();
    window.addEventListener('zifek-rates-loaded', onRates);
    return () => window.removeEventListener('zifek-rates-loaded', onRates);
  }, [sync]);

  const forceRefresh = async () => {
    setRefreshing(true);
    setLastResult(null);
    const ok = await loadExchangeRates(true);
    sync();
    setLastResult(ok ? 'ok' : 'fail');
    setRefreshing(false);
    setTimeout(() => setLastResult(null), 4000);
  };

  const sourceLabel: Record<RateSource, { label: string; className: string; icon: string }> = {
    live: { label: 'API en direct', className: 'bg-accent-50 text-accent-700', icon: 'ri-checkbox-circle-line' },
    cache: { label: 'Cache (12 h)', className: 'bg-secondary-50 text-secondary-700', icon: 'ri-time-line' },
    static: { label: 'Taux de repli', className: 'bg-amber-50 text-amber-700', icon: 'ri-error-warning-line' },
  };
  const src = sourceLabel[meta.source];

  const rows = CURRENCIES.map((c) => ({
    ...c,
    rate: snapshot.rates[c.code],
  }));

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Journal des taux de change</h2>
          <p className="text-sm text-foreground-500 mt-1">
            État de l&apos;API de taux en direct et rafraîchissement manuel
          </p>
        </div>
        <button
          onClick={forceRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
        >
          {refreshing ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Rafraîchissement...
            </>
          ) : (
            <>
              <i className="ri-refresh-line"></i>
              Forcer le rafraîchissement
            </>
          )}
        </button>
      </div>

      {/* Statut */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <span className="text-xs text-foreground-500 flex items-center gap-1.5 mb-2">
            <i className="ri-radar-line"></i>Statut de l&apos;API
          </span>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${src.className}`}>
            <i className={src.icon}></i>{src.label}
          </span>
          <p className="text-xs text-foreground-400 mt-2">
            Source&nbsp;: <span className="font-mono">open.er-api.com</span> (gratuit, sans clé)
          </p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <span className="text-xs text-foreground-500 flex items-center gap-1.5 mb-2">
            <i className="ri-history-line"></i>Dernière mise à jour
          </span>
          <span className="text-base font-semibold text-foreground-900">
            {meta.updatedAt
              ? new Date(meta.updatedAt).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
              : '—'}
          </span>
          <p className="text-xs text-foreground-400 mt-2">Cache renouvelé automatiquement toutes les 12 h.</p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <span className="text-xs text-foreground-500 flex items-center gap-1.5 mb-2">
            <i className="ri-exchange-dollar-line"></i>Taux disponibles
          </span>
          <span className="text-base font-semibold text-foreground-900">
            {Object.keys(snapshot.rates).length} devises
          </span>
          <p className="text-xs text-foreground-400 mt-2">Base de conversion&nbsp;: 1 EUR.</p>
        </div>
      </div>

      {lastResult && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 ${
          lastResult === 'ok' ? 'bg-accent-50 text-accent-700' : 'bg-amber-50 text-amber-700'
        }`}>
          <i className={lastResult === 'ok' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
          {lastResult === 'ok'
            ? 'Taux actualisés depuis l\u2019API en direct.'
            : 'API indisponible — conservation des taux de repli.'}
        </div>
      )}

      {/* Table des taux */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-background-200/70 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground-800">Taux par devise (pour 1 EUR)</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${light ? 'bg-accent-50 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
            {light ? 'Taux live' : 'Taux indicatifs'}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <div key={r.code} className="flex items-center justify-between px-5 py-3 border-b border-background-100">
              <span className="flex items-center gap-2 text-sm text-foreground-800">
                <span className="text-base">{r.flag}</span>
                <span className="font-medium">{r.code}</span>
                <span className="text-xs text-foreground-400 hidden sm:inline">{r.label}</span>
              </span>
              <span className="text-sm font-semibold text-foreground-900 tabular-nums">
                {r.rate ? r.rate.toLocaleString('fr-FR', { maximumFractionDigits: 4 }) : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-foreground-400 mt-4">
        <i className="ri-information-line mr-1"></i>
        La devise de référence de chaque boutique reste celle choisie par le marchand ; ces taux servent uniquement à
        convertir les prix pour les visiteurs d&apos;autres pays.
      </p>
    </div>
  );
}